/**
 * TradeFinder Settings tab data: flow-signals' schema, per-root settings
 * layers and the self-tuner file, through the engine's proxy
 * (utt-engine/src/flow_signals/settings.rs). Shapes and pure logic:
 * components/TradeFinder/flowSettings.ts.
 *
 * Plain request/response: no socket, timer or subscription is held, so
 * unlike flowSignalsStore / flowLinesStore there is nothing to reference-count.
 * The tab fetches on mount / root change; a late answer for a root that was
 * re-requested since is dropped (per-root sequence numbers).
 *
 * Unsaved edits (drafts) live here too, keyed by root, so they survive the
 * panel closing (outside click, Escape) and tab switches; the Settings tab
 * label shows a dot while any root has some. Cleared on save or discard.
 */
import { create } from 'zustand';
import { getApiBaseUrl } from '../config/ports';
import type { FlowDraft, FlowDrafts, FlowParam, FlowPutBody, FlowSettings, FlowTune } from '../components/TradeFinder/flowSettings';

export interface FlowRootSettingsState {
  data: FlowSettings | null;
  loading: boolean;
  error: string | null;
}

export type FlowTuneState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ok'; data: FlowTune }
  /** 404: no autotune file yet. */
  | { status: 'none'; message: string }
  | { status: 'error'; message: string };

export type FlowSaveResult =
  | { ok: true; data: FlowSettings }
  | { ok: false; status: number; error: string; path?: string };

interface FlowSettingsStoreState {
  schema: FlowParam[] | null;
  schemaError: string | null;
  byRoot: Record<string, FlowRootSettingsState>;
  tune: FlowTuneState;
  /** Unsaved edits per root; a root with none has no entry. */
  drafts: Record<string, FlowDrafts>;

  /** Set (or with null drop) the pending value for one path. */
  setDraft: (root: string, path: string, draft: FlowDraft | null) => void;
  /** Set (or with null drop) the pending pin state for one path. */
  setPinDraft: (root: string, path: string, pinned: boolean | null) => void;
  clearDrafts: (root: string) => void;

  fetchSchema: () => Promise<void>;
  fetchSettings: (root: string) => Promise<void>;
  fetchTune: () => Promise<void>;
  /** One PUT; on success the caller refetches. */
  save: (root: string, body: FlowPutBody) => Promise<FlowSaveResult>;
}

const seq = new Map<string, number>();
const nextSeq = (key: string): number => {
  const n = (seq.get(key) ?? 0) + 1;
  seq.set(key, n);
  return n;
};
const isCurrent = (key: string, n: number): boolean => seq.get(key) === n;

async function readJson(res: Response): Promise<Record<string, unknown> | unknown[] | null> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

/** `{error}` from a JSON body, else a status line. */
export function errorText(status: number, body: unknown): string {
  const e = body && typeof body === 'object' && !Array.isArray(body) ? (body as { error?: unknown }).error : undefined;
  if (typeof e === 'string' && e) return e;
  if (status === 503) return 'flow-signals is not reachable';
  return `HTTP ${status}`;
}

const networkError = (e: unknown): string => `engine not reachable (${e instanceof Error ? e.message : String(e)})`;

const NO_DRAFTS: FlowDrafts = { values: {}, pins: {} };

/** A root's drafts (a shared empty object when it has none). */
export function draftsFor(drafts: Record<string, FlowDrafts>, root: string): FlowDrafts {
  return drafts[root] ?? NO_DRAFTS;
}

const hasAny = (d: FlowDrafts | undefined): boolean =>
  !!d && (Object.keys(d.values).length > 0 || Object.keys(d.pins).length > 0);

/** Roots with unsaved edits. */
export function dirtyRoots(drafts: Record<string, FlowDrafts>): string[] {
  return Object.keys(drafts).filter((r) => hasAny(drafts[r]));
}

/** Store `drafts` with one root replaced; a root left empty is dropped. */
function withRoot(drafts: Record<string, FlowDrafts>, root: string, next: FlowDrafts): Record<string, FlowDrafts> {
  const out = { ...drafts };
  if (hasAny(next)) out[root] = next;
  else delete out[root];
  return out;
}

export const useFlowSettingsStore = create<FlowSettingsStoreState>((set) => ({
  schema: null,
  schemaError: null,
  byRoot: {},
  tune: { status: 'idle' },
  drafts: {},

  setDraft: (root, path, draft) =>
    set((s) => {
      const cur = draftsFor(s.drafts, root);
      if (draft === null && !(path in cur.values)) return s;
      const values = { ...cur.values };
      if (draft === null) delete values[path];
      else values[path] = draft;
      return { drafts: withRoot(s.drafts, root, { ...cur, values }) };
    }),

  setPinDraft: (root, path, pinned) =>
    set((s) => {
      const cur = draftsFor(s.drafts, root);
      if (pinned === null && !(path in cur.pins)) return s;
      const pins = { ...cur.pins };
      if (pinned === null) delete pins[path];
      else pins[path] = pinned;
      return { drafts: withRoot(s.drafts, root, { ...cur, pins }) };
    }),

  clearDrafts: (root) =>
    set((s) => (s.drafts[root] ? { drafts: withRoot(s.drafts, root, NO_DRAFTS) } : s)),

  fetchSchema: async () => {
    const n = nextSeq('schema');
    try {
      const res = await fetch(`${getApiBaseUrl()}/flow-schema`);
      const body = await readJson(res);
      if (!isCurrent('schema', n)) return;
      if (!res.ok || !Array.isArray(body)) {
        set({ schemaError: res.ok ? 'unexpected schema' : errorText(res.status, body) });
        return;
      }
      set({ schema: body as FlowParam[], schemaError: null });
    } catch (e) {
      if (isCurrent('schema', n)) set({ schemaError: networkError(e) });
    }
  },

  fetchSettings: async (root) => {
    const key = `settings:${root}`;
    const n = nextSeq(key);
    set((s) => ({ byRoot: { ...s.byRoot, [root]: { data: s.byRoot[root]?.data ?? null, loading: true, error: null } } }));
    const done = (patch: Partial<FlowRootSettingsState>) =>
      set((s) => ({
        byRoot: { ...s.byRoot, [root]: { data: s.byRoot[root]?.data ?? null, error: null, ...patch, loading: false } },
      }));
    try {
      const res = await fetch(`${getApiBaseUrl()}/flow-settings/${encodeURIComponent(root)}`);
      const body = await readJson(res);
      if (!isCurrent(key, n)) return;
      if (!res.ok || !body || Array.isArray(body) || typeof (body as unknown as FlowSettings).layers !== 'object') {
        done({ error: res.ok ? 'unexpected settings' : errorText(res.status, body) });
        return;
      }
      done({ data: body as unknown as FlowSettings });
    } catch (e) {
      if (isCurrent(key, n)) done({ error: networkError(e) });
    }
  },

  fetchTune: async () => {
    const n = nextSeq('tune');
    set({ tune: { status: 'loading' } });
    try {
      const res = await fetch(`${getApiBaseUrl()}/flow-tune`);
      const body = await readJson(res);
      if (!isCurrent('tune', n)) return;
      if (res.status === 404) {
        set({ tune: { status: 'none', message: errorText(404, body) } });
      } else if (!res.ok || !body || Array.isArray(body)) {
        set({ tune: { status: 'error', message: errorText(res.status, body) } });
      } else {
        set({ tune: { status: 'ok', data: body as unknown as FlowTune } });
      }
    } catch (e) {
      if (isCurrent('tune', n)) set({ tune: { status: 'error', message: networkError(e) } });
    }
  },

  save: async (root, body) => {
    try {
      const res = await fetch(`${getApiBaseUrl()}/flow-settings/${encodeURIComponent(root)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const json = await readJson(res);
      if (!res.ok) {
        const path = json && !Array.isArray(json) && typeof json.path === 'string' ? json.path : undefined;
        return { ok: false, status: res.status, error: errorText(res.status, json), path };
      }
      return { ok: true, data: json as unknown as FlowSettings };
    } catch (e) {
      return { ok: false, status: 0, error: networkError(e) };
    }
  },
}));
